/**
 * Templates (flag `templates`): the gallery sheet (filters, search, the list, a preview and "Use template"), the
 * "Save as template" sheet, the "editing template" banner and a row template's default values. Every sheet renders
 * TemplatesViewModel / RowTemplateViewModel state and calls their intents.
 */
import type { PageNode, PageTemplateInfo, TemplateCategory, TemplateOutlineLine, TemplatePreviewNode, TemplateRef } from "@bundle/contracts";
import { Db, Templates, type TemplateFilter, type TemplatesState, type TemplatesViewModel } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useState } from "react";
import { Button, EmojiPicker, Icon, IconButton, Popover, Pressable, ScrollView, Sheet, Stack, Text, TextField, Toast, Toggle } from "../primitives/index.ts";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { useRowTemplate } from "../context/TemplatesContext.tsx";
import { layoutIcon, typeIcon, type CellContext } from "./database/labels.ts";
import { PropertyCell } from "./database/PropertyCell.tsx";

const CATEGORY_ICONS: Record<TemplateCategory, IconName> = {
  projects: "board",
  meetings: "users",
  knowledge: "notes",
  personal: "user",
  business: "tag",
};

const FILTER_ICONS: Record<TemplateFilter, IconName> = { all: "sparkles", saved: "bookmark", ...CATEGORY_ICONS };

function Pill(p: { label: string; selected: boolean; onPress: () => void; icon?: IconName; disabled?: boolean; testID: string }) {
  return (
    <Pressable radius="full" pressed={p.selected} disabled={p.disabled} onPress={p.onPress} testID={p.testID}>
      <Stack direction="horizontal" align="center" gap="1.5" paddingX="3" paddingY="1" radius="full" border={p.selected ? "accent.text" : "border.subtle"}>
        {p.icon === undefined ? null : <Icon name={p.icon} size="icon.sm" color={p.selected ? "text.link" : "text.tertiary"} />}
        <Text variant="footnote" weight="medium" color={p.selected ? "text.link" : "text.secondary"} lines={1}>
          {p.label}
        </Text>
      </Stack>
    </Pressable>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <Text variant="footnote" weight="medium" color="text.secondary">
      {label}
    </Text>
  );
}

/** An emoji icon (content) or an icon-font name ("icon:board"). */
function TemplateIcon({ icon, large = false }: { icon: string; large?: boolean }) {
  if (icon.startsWith("icon:")) return <Icon name={icon.slice(5) as IconName} size={large ? "icon.lg" : "icon.md"} color="text.secondary" />;
  return <Text variant={large ? "title1" : "headline"}>{icon}</Text>;
}

// ---------- Gallery ----------

export type TemplateGallerySheetProps = {
  vm: TemplatesViewModel;
  state: TemplatesState;
  /** A page was created from a template: open it. */
  onCreated: (page: PageNode) => void;
  /** Open a saved template's page to edit it ("editing template" mode). */
  onEdit: (templateId: string) => void;
};

/** The template gallery: filters, search, the list, a preview, the destination and "Use template". */
export function TemplateGallerySheet({ vm, state: s, onCreated, onEdit }: TemplateGallerySheetProps) {
  const t = useT();
  const tr = useTr();
  const filters: TemplateFilter[] = ["all", ...(s.templates.some((x) => x.scope !== "builtin") ? (["saved"] as const) : []), ...Templates.TEMPLATE_CATEGORIES];
  const filterLabel = (f: TemplateFilter) => (f === "all" ? t("templates.filter.all") : f === "saved" ? t("templates.filter.saved") : tr(Templates.categoryTitle(f)));
  const use = () => {
    void vm.use().then((page) => {
      if (page !== undefined) onCreated(page);
    });
  };

  return (
    <Sheet open={s.open} onClose={() => vm.close()} title={t("templates.title")} size="large" closeLabel={t("common.close")} testID="template-gallery">
      <Stack grow gap="3">
        <Stack>
        <TextField
          value={s.query}
          onChange={(q) => vm.setQuery(q)}
          placeholder={t("templates.search")}
          accessibilityLabel={t("templates.search")}
          leadingIcon="search"
          autoFocus
          onSubmit={use}
          onCancel={() => (s.query === "" ? vm.close() : vm.setQuery(""))}
          testID="template-search"
        />
        </Stack>
        <Stack grow direction="horizontal" gap="4">
          <Stack width="property.label" gap="0.5" testID="template-filters">
            {filters.map((f) => (
              <Pressable key={f} direction="horizontal" radius="md" current={s.filter === f} onPress={() => vm.setFilter(f)} testID={`template-filter-${f}`}>
                <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1.5">
                  <Icon name={FILTER_ICONS[f]} size="icon.sm" color={s.filter === f ? "text.primary" : "text.tertiary"} />
                  <Text variant="footnote" weight={s.filter === f ? "semibold" : "regular"} lines={1}>
                    {filterLabel(f)}
                  </Text>
                </Stack>
              </Pressable>
            ))}
            {s.canManageSettings && s.filter === "saved" ? (
              <Stack gap="1.5" paddingX="2" paddingY="3" testID="template-settings">
                <FieldLabel label={t("templates.settings.creators")} />
                <Stack direction="horizontal" gap="1" wrap>
                  <Pill label={t("templates.settings.admins")} selected={s.settings.workspaceTemplateCreators === "admins"} disabled={s.busy} onPress={() => void vm.setWorkspaceCreators("admins")} testID="template-settings-admins" />
                  <Pill label={t("templates.settings.members")} selected={s.settings.workspaceTemplateCreators === "members"} disabled={s.busy} onPress={() => void vm.setWorkspaceCreators("members")} testID="template-settings-members" />
                </Stack>
              </Stack>
            ) : null}
          </Stack>
          <ScrollView testID="template-list">
            <TemplateList vm={vm} state={s} />
          </ScrollView>
          <Stack width="picker.width" gap="3" testID="template-preview">
            {s.selected === undefined ? (
              <Stack grow align="center" justify="center">
                <Text variant="footnote" color="text.tertiary" align="center">
                  {t("templates.preview.none")}
                </Text>
              </Stack>
            ) : (
              <>
                <ScrollView>
                  <TemplatePreview template={s.selected} vm={vm} state={s} onEdit={onEdit} />
                </ScrollView>
                <UseTemplateFooter vm={vm} state={s} onUse={use} />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Sheet>
  );
}

function TemplateList({ vm, state: s }: { vm: TemplatesViewModel; state: TemplatesState }) {
  const t = useT();
  const tr = useTr();
  if (s.status === "loading" && s.templates.length === 0) {
    return (
      <Stack padding="4">
        <Text color="text.tertiary">{t("common.loading")}</Text>
      </Stack>
    );
  }
  if (s.status === "error" && s.templates.length === 0) {
    return (
      <Stack align="center" gap="3" padding="6" testID="template-list-error">
        <Icon name="offline" size="icon.lg" color="text.tertiary" />
        <Text variant="footnote" color="text.secondary" align="center">
          {t("templates.loadFailed")}
        </Text>
        <Button label={t("common.retry")} onPress={() => void vm.refresh()} testID="template-list-retry" />
      </Stack>
    );
  }
  if (s.sections.length === 0) {
    return (
      <Stack padding="4" testID="template-list-empty">
        <Text variant="footnote" color="text.tertiary">
          {t("templates.empty", { query: s.query })}
        </Text>
      </Stack>
    );
  }
  return (
    <Stack gap="4" paddingY="1">
      {s.sections.map((section) => (
        <Stack key={section.id} gap="1" testID={`template-section-${section.id}`}>
          <Stack paddingX="2">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {tr(section.title)}
            </Text>
          </Stack>
          {section.templates.map((x) => (
            <Pressable key={x.ref} direction="horizontal" radius="md" current={s.selected?.ref === x.ref} onPress={() => vm.select(x.ref)} accessibilityLabel={x.name} testID={`template-${x.ref}`}>
              <Stack grow direction="horizontal" align="center" gap="3" paddingX="2" paddingY="2">
                <Stack width="avatar.md" align="center">
                  <TemplateIcon icon={x.icon} />
                </Stack>
                <Stack grow gap="0.5">
                  <Stack direction="horizontal" align="center" gap="2">
                    <Text weight="semibold" lines={1}>
                      {x.name}
                    </Text>
                    {x.scope === "builtin" ? null : <Icon name={x.scope === "workspace" ? "globe" : "lock"} size="icon.sm" color="text.tertiary" />}
                  </Stack>
                  <Text variant="footnote" color="text.secondary" lines={2}>
                    {x.description}
                  </Text>
                </Stack>
              </Stack>
            </Pressable>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function OutlineLine({ line }: { line: TemplateOutlineLine }) {
  const indent = line.depth === undefined ? undefined : line.depth > 1 ? "8" : "4";
  const body = (() => {
    switch (line.type) {
      case "heading1":
      case "heading2":
        return (
          <Text variant="callout" weight="semibold" lines={1}>
            {line.text}
          </Text>
        );
      case "heading3":
        return (
          <Text variant="footnote" weight="semibold" lines={1}>
            {line.text}
          </Text>
        );
      case "todo":
        return (
          <Stack direction="horizontal" align="center" gap="1.5">
            <Icon name={line.checked === true ? "checkbox" : "square"} size="icon.sm" color="text.tertiary" />
            <Text variant="footnote" color="text.secondary" lines={1}>
              {line.text}
            </Text>
          </Stack>
        );
      case "callout":
        return (
          <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" radius="md" background="bg.surface">
            {line.icon === undefined ? <Icon name="sparkles" size="icon.sm" color="text.secondary" /> : <Text variant="footnote">{line.icon}</Text>}
            <Text variant="footnote" color="text.secondary" lines={2}>
              {line.text}
            </Text>
          </Stack>
        );
      case "toggle":
        return (
          <Stack direction="horizontal" align="center" gap="1.5">
            <Icon name="chevronRight" size="icon.sm" color="text.tertiary" />
            <Text variant="footnote" color="text.secondary" lines={1}>
              {line.text}
            </Text>
          </Stack>
        );
      case "bulleted":
      case "numbered":
        return (
          <Text variant="footnote" color="text.secondary" lines={1}>
            {`• ${line.text}`}
          </Text>
        );
      case "child_page":
      case "child_database":
        return (
          <Stack direction="horizontal" align="center" gap="1.5">
            <Icon name={line.type === "child_database" ? "database" : "page"} size="icon.sm" color="text.tertiary" />
            <Text variant="footnote" color="text.link" lines={1}>
              {line.text}
            </Text>
          </Stack>
        );
      case "divider":
        return <Stack height="icon.sm" />;
      default:
        return (
          <Text variant="footnote" color="text.secondary" lines={2}>
            {line.text}
          </Text>
        );
    }
  })();
  return indent === undefined ? body : <Stack paddingX={indent}>{body}</Stack>;
}

function PreviewNode({ node }: { node: TemplatePreviewNode }) {
  const t = useT();
  const indent = node.depth === 0 ? undefined : node.depth === 1 ? "4" : "8";
  const content = (
    <Stack gap="2" padding="3" radius="md" border="border.subtle">
      <Stack direction="horizontal" align="center" gap="2">
        {node.icon === undefined ? <Icon name={node.kind === "database" ? "database" : "page"} size="icon.md" color="text.secondary" /> : <Text variant="headline">{node.icon}</Text>}
        <Text weight="semibold" lines={1}>
          {node.title}
        </Text>
      </Stack>
      {node.outline === undefined ? null : (
        <Stack gap="1">
          {node.outline.map((l, i) => (
            <OutlineLine key={i} line={l} />
          ))}
        </Stack>
      )}
      {node.database === undefined ? null : (
        <Stack gap="2">
          <Stack direction="horizontal" gap="1" wrap>
            {node.database.views.map((v, i) => (
              <Stack key={i} direction="horizontal" align="center" gap="1" paddingX="2" paddingY="0.5" radius="full" background="chip.bg">
                <Icon name={layoutIcon(v.layout)} size="icon.sm" color="text.tertiary" />
                <Text variant="caption" color="chip.fg">
                  {v.name}
                </Text>
              </Stack>
            ))}
          </Stack>
          <Stack gap="0.5">
            <Text variant="caption" weight="semibold" color="text.tertiary">
              {t("templates.preview.properties")}
            </Text>
            {node.database.properties.map((p, i) => (
              <Stack key={i} direction="horizontal" align="center" gap="1.5">
                <Icon name={typeIcon(p.type)} size="icon.sm" color="text.tertiary" />
                <Text variant="footnote" lines={1}>
                  {p.name}
                </Text>
                {p.options === undefined ? null : (
                  <Text variant="caption" color="text.tertiary" lines={1}>
                    {p.options.join(" · ")}
                  </Text>
                )}
              </Stack>
            ))}
          </Stack>
          {node.database.rowCount === 0 ? null : (
            <Stack gap="0.5">
              <Text variant="caption" weight="semibold" color="text.tertiary">
                {t("templates.preview.rows", { count: node.database.rowCount })}
              </Text>
              {node.database.sampleRows.map((r, i) => (
                <Text key={i} variant="footnote" color="text.secondary" lines={1}>
                  {`• ${r}`}
                </Text>
              ))}
            </Stack>
          )}
          {node.database.rowTemplates === undefined ? null : (
            <Stack gap="0.5">
              <Text variant="caption" weight="semibold" color="text.tertiary">
                {t("templates.preview.rowTemplates")}
              </Text>
              <Text variant="footnote" color="text.secondary">
                {node.database.rowTemplates.join(" · ")}
              </Text>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
  return indent === undefined ? content : <Stack paddingX={indent}>{content}</Stack>;
}

function TemplatePreview({ template: x, vm, state: s, onEdit }: { template: PageTemplateInfo; vm: TemplatesViewModel; state: TemplatesState; onEdit: (templateId: string) => void }) {
  const t = useT();
  const [renaming, setRenaming] = useState<string | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const scope = x.scope === "builtin" ? t("templates.scope.builtin") : x.scope === "workspace" ? t("templates.scope.workspace") : t("templates.scope.personal");
  return (
    <Stack gap="3" testID="template-preview-body">
      <Stack direction="horizontal" align="center" gap="3">
        <TemplateIcon icon={x.icon} large />
        <Stack grow gap="0.5">
          {renaming === undefined ? (
            <Text variant="title3" weight="bold">
              {x.name}
            </Text>
          ) : (
            <TextField
              value={renaming}
              onChange={setRenaming}
              autoFocus
              placeholder={t("templates.rename")}
              accessibilityLabel={t("templates.rename")}
              onSubmit={() => {
                void vm.rename(x.id!, renaming);
                setRenaming(undefined);
              }}
              onCancel={() => setRenaming(undefined)}
              testID="template-rename-input"
            />
          )}
          <Text variant="caption" color="text.tertiary">
            {scope}
          </Text>
        </Stack>
      </Stack>
      <Text variant="footnote" color="text.secondary">
        {x.description}
      </Text>
      {x.canManage && x.id !== undefined ? (
        <Stack direction="horizontal" gap="1" wrap testID="template-manage">
          <Button label={t("templates.edit")} icon="edit" size="sm" onPress={() => onEdit(x.id!)} testID="template-edit" />
          <Button label={t("templates.rename")} size="sm" variant="ghost" onPress={() => setRenaming(x.name)} testID="template-rename" />
          <Button label={t("templates.delete")} icon="trash" size="sm" variant="ghost" onPress={() => setConfirmDelete(true)} testID="template-delete" />
        </Stack>
      ) : null}
      {confirmDelete && x.id !== undefined ? (
        <Stack gap="2" padding="3" radius="md" background="bg.surface" testID="template-delete-confirm">
          <Text variant="footnote">{t("templates.delete.confirm", { name: x.name })}</Text>
          <Stack direction="horizontal" gap="2">
            <Button label={t("common.cancel")} size="sm" onPress={() => setConfirmDelete(false)} testID="template-delete-cancel" />
            <Button
              label={t("common.delete")}
              size="sm"
              variant="destructive"
              loading={s.busy}
              onPress={() => {
                void vm.remove(x.id!).then(() => setConfirmDelete(false));
              }}
              testID="template-delete-ok"
            />
          </Stack>
        </Stack>
      ) : null}
      {x.preview.length === 0 ? null : (
        <Stack gap="2">
          <Text variant="caption" weight="semibold" color="text.tertiary">
            {t("templates.preview.creates")}
          </Text>
          {x.preview.map((n, i) => (
            <PreviewNode key={i} node={n} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function UseTemplateFooter({ vm, state: s, onUse }: { vm: TemplatesViewModel; state: TemplatesState; onUse: () => void }) {
  const t = useT();
  const tr = useTr();
  return (
    <Stack gap="2" paddingY="2" testID="template-use">
      <FieldLabel label={t("templates.destination")} />
      <Stack direction="horizontal" gap="1" wrap>
        {s.canCreatePages ? (
          <>
            <Pill label={t("templates.destination.workspace")} icon="globe" selected={s.destination === "workspace"} onPress={() => vm.setDestination("workspace")} testID="template-dest-workspace" />
            <Pill label={t("templates.destination.private")} icon="lock" selected={s.destination === "private"} onPress={() => vm.setDestination("private")} testID="template-dest-private" />
          </>
        ) : null}
        {s.context === undefined ? null : (
          <Pill label={t("templates.destination.inside", { title: s.context.title === "" ? t("page.untitled") : s.context.title })} icon="page" selected={s.destination === "inside"} onPress={() => vm.setDestination("inside")} testID="template-dest-inside" />
        )}
      </Stack>
      {s.selected?.hasSampleRows === true ? <Toggle label={t("templates.startEmpty")} value={s.startEmpty} onChange={(v) => vm.setStartEmpty(v)} testID="template-start-empty" /> : null}
      {s.error === undefined ? null : (
        <Text variant="caption" color="status.danger" testID="template-error">
          {tr(s.error)}
        </Text>
      )}
      <Button label={t("templates.use")} variant="primary" loading={s.creating} disabled={!s.canCreatePages && s.context === undefined} onPress={onUse} testID="template-use-button" />
    </Stack>
  );
}

// ---------- Save as template ----------

export type SaveTemplateSheetProps = { vm: TemplatesViewModel; state: TemplatesState };

/** Name, description, icon, category, who can use it, and whether database rows come along. */
export function SaveTemplateSheet({ vm, state: s }: SaveTemplateSheetProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const [pickingIcon, setPickingIcon] = useState(false);
  const d = s.save;
  return (
    <Sheet open={d !== undefined} onClose={() => vm.cancelSave()} title={t("templates.save.title")} closeLabel={t("common.close")} testID="save-template-sheet">
      {d === undefined ? null : (
        <Stack gap="4">
          <Stack direction="horizontal" align="end" gap="2">
            <Popover
              open={pickingIcon}
              onClose={() => setPickingIcon(false)}
              side="bottom"
              align="start"
              anchor={
                <Pressable radius="md" onPress={() => setPickingIcon(true)} accessibilityLabel={t("db.icon.change")} testID="save-template-icon">
                  <Stack padding="1.5" radius="md" border="border.subtle">
                    {d.icon === undefined ? <Icon name="page" size="icon.lg" color="text.secondary" /> : <TemplateIcon icon={d.icon} large />}
                  </Stack>
                </Pressable>
              }
            >
              <EmojiPicker
                locale={locale}
                onSelect={(e) => {
                  vm.setSaveDraft({ icon: e });
                  setPickingIcon(false);
                }}
                testID="save-template-emoji"
              />
            </Popover>
            <Stack grow>
              <TextField label={t("templates.save.name")} value={d.name} onChange={(v) => vm.setSaveDraft({ name: v })} autoFocus onSubmit={() => void vm.submitSave()} testID="save-template-name" />
            </Stack>
          </Stack>
          <TextField label={t("templates.save.description")} value={d.description} onChange={(v) => vm.setSaveDraft({ description: v })} multiline testID="save-template-description" />
          <Stack gap="1.5">
            <FieldLabel label={t("templates.save.category")} />
            <Stack direction="horizontal" gap="1" wrap>
              {Templates.TEMPLATE_CATEGORIES.map((c) => (
                <Pill key={c} label={tr(Templates.categoryTitle(c))} icon={CATEGORY_ICONS[c]} selected={d.category === c} onPress={() => vm.setSaveDraft({ category: c })} testID={`save-template-category-${c}`} />
              ))}
            </Stack>
          </Stack>
          <Stack gap="1.5">
            <FieldLabel label={t("templates.save.scope")} />
            <Stack direction="horizontal" gap="1" wrap>
              <Pill label={t("templates.save.scope.personal")} icon="lock" selected={d.scope === "personal"} onPress={() => vm.setSaveDraft({ scope: "personal" })} testID="save-template-scope-personal" />
              <Pill
                label={t("templates.save.scope.workspace")}
                icon="globe"
                selected={d.scope === "workspace"}
                disabled={!s.canCreateWorkspace}
                onPress={() => vm.setSaveDraft({ scope: "workspace" })}
                testID="save-template-scope-workspace"
              />
            </Stack>
            {s.canCreateWorkspace ? null : (
              <Text variant="caption" color="text.tertiary">
                {t("templates.save.workspaceAdminsOnly")}
              </Text>
            )}
          </Stack>
          {d.hasDatabases ? (
            <Toggle label={t("templates.save.includeRows")} value={d.includeRows} onChange={(v) => vm.setSaveDraft({ includeRows: v })} testID="save-template-rows" />
          ) : null}
          {d.error === undefined ? null : (
            <Text variant="caption" color="status.danger" testID="save-template-error">
              {tr(d.error)}
            </Text>
          )}
          <Stack direction="horizontal" justify="end" gap="2">
            <Button label={t("common.cancel")} onPress={() => vm.cancelSave()} testID="save-template-cancel" />
            <Button label={t("templates.save.submit")} variant="primary" loading={d.saving} disabled={d.name.trim() === ""} onPress={() => void vm.submitSave()} testID="save-template-submit" />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}

/** Confirmations from the templates view model (saved, duplicate failed). */
export function TemplateNotice({ vm, state: s }: { vm: TemplatesViewModel; state: TemplatesState }) {
  const t = useT();
  const tr = useTr();
  return <Toast open={s.notice !== undefined} onClose={() => vm.dismissNotice()} message={s.notice === undefined ? "" : tr(s.notice)} closeLabel={t("common.close")} durationMs={5000} testID="template-notice" />;
}

// ---------- Editing a template ----------

export type TemplateEditingBannerProps = {
  template: TemplateRef;
  /** "Done": back to where the template is used (the gallery, or the database). */
  onDone: () => void;
  testID?: string;
};

/** The bar above a template's page while it is being edited (or viewed read-only). */
export function TemplateEditingBanner({ template, onDone, testID = "template-banner" }: TemplateEditingBannerProps) {
  const t = useT();
  const text = !template.canEdit ? t("templates.editing.readOnly") : template.kind === "row" ? t("templates.editing.rowBanner", { name: template.name }) : t("templates.editing.banner", { name: template.name });
  return (
    <Stack direction="horizontal" align="center" gap="3" paddingX="4" paddingY="2" radius="md" background="accent.subtle" testID={testID}>
      <Icon name="sparkles" size="icon.md" color="text.link" />
      <Stack grow>
        <Text variant="footnote" color="text.primary">
          {text}
        </Text>
      </Stack>
      <Button label={t("templates.editing.done")} size="sm" variant="primary" onPress={onDone} testID={`${testID}-done`} />
    </Stack>
  );
}

export type RowTemplateDefaultsPanelProps = { databaseId: string; templateId: string; readOnly: boolean; testID?: string };

/** A row template's default values: one editable cell per property (title included). */
export function RowTemplateDefaultsPanel({ databaseId, templateId, readOnly, testID = "row-template-defaults" }: RowTemplateDefaultsPanelProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const rt = useRowTemplate(databaseId, templateId, readOnly);
  if (rt === null || rt.state.row === undefined || rt.state.database === undefined) return <Stack testID={`${testID}-loading`} />;
  const { vm, state } = rt;
  const row = state.row!;
  const ctx: CellContext = {
    people: state.people,
    titles: state.titles,
    relationOptions: state.relationOptions,
    weekStartsOn: Db.weekStartFor(locale),
    tzOffsetMinutes: state.tzOffsetMinutes,
    readOnly: state.readOnly,
  };
  return (
    <Stack gap="1" padding="3" radius="md" border="border.subtle" testID={testID}>
      <Stack direction="horizontal" align="center" gap="2">
        <Text variant="footnote" weight="semibold">
          {t("templates.rowTemplates.defaults")}
        </Text>
        {state.saving ? <Icon name="clock" size="icon.sm" color="text.tertiary" /> : null}
      </Stack>
      <Text variant="caption" color="text.tertiary">
        {t("templates.rowTemplates.defaults.hint")}
      </Text>
      {state.properties.map((p) => (
        <Stack key={p.id} direction="horizontal" align="center" gap="2" testID={`${testID}-${p.id}`}>
          <Stack width="property.label" direction="horizontal" align="center" gap="1.5">
            <Icon name={typeIcon(p.type)} size="icon.sm" color="text.tertiary" />
            <Text variant="footnote" color="text.secondary" lines={1}>
              {p.name}
            </Text>
          </Stack>
          <Stack grow>
            <PropertyCell prop={p} row={row} ctx={ctx} variant="panel" onChange={(v) => void vm.setValue(p.id, v)} onCreateOption={() => undefined} testID={`${testID}-cell-${p.id}`} />
          </Stack>
          {readOnly || row.values[p.id] === undefined ? null : (
            <IconButton icon="close" size="sm" accessibilityLabel={t("templates.rowTemplates.clear")} onPress={() => void vm.setValue(p.id, null)} testID={`${testID}-clear-${p.id}`} />
          )}
        </Stack>
      ))}
      {state.error === undefined ? null : (
        <Text variant="caption" color="status.danger">
          {tr(state.error)}
        </Text>
      )}
    </Stack>
  );
}
